// ProjectFinance.vue
<template>
  <div class="project-finance">
    
    <!-- Main Content Grid -->
    <div class="finance-content-grid">
      
      <!-- Balance Card with Modern Design -->
      <div class="balance-card-modern">
        <div class="balance-card-background">
          <div class="balance-card-content">
            <div class="balance-label">{{ $t('finance.currentBalance') }}</div>
            <div class="balance-amount" :class="{ 'negative': projectBalance < 0 }">
              {{ formattedBalance }}
            </div>
            <div class="balance-actions">
              <button class="btn btn-primary" @click="openPaymentModal">
                <i class="fas fa-plus"></i>
                {{ $t('finance.recordPayment') }}
              </button>
              <PermissionGuard :permission="'MANAGE_EXPENSE'">
                <button class="btn btn-secondary" @click="openExpenseModal">
                  <i class="fas fa-minus"></i>
                  {{ $t('finance.recordExpense') }}
                </button>
              </PermissionGuard>
              <PermissionGuard :permission="'REQUEST_FINANCE_PROVISIONING'">
                <button class="btn btn-info" @click="openProvisioningModal">
                  <i class="fas fa-hand-holding-usd"></i>
                  {{ $t('provisioning.submit') }}
                </button>
              </PermissionGuard>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Grid -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-header">
            <span class="stat-label">{{ $t('finance.totalPayments') }}</span>
            <div class="stat-icon green">
              <i class="fas fa-arrow-down"></i>
            </div>
          </div>
          <div class="stat-value">{{ formattedTotalPayments }}</div>
          <div class="stat-change positive">
            <i class="fas fa-check-circle"></i>
            {{ paymentsCount }} {{ $t('finance.transactions') }}
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-header">
            <span class="stat-label">{{ $t('finance.totalExpenses') }}</span>
            <div class="stat-icon red">
              <i class="fas fa-arrow-up"></i>
            </div>
          </div>
          <div class="stat-value">{{ formattedTotalExpenses }}</div>
          <div class="stat-change negative">
            <i class="fas fa-exclamation-circle"></i>
            {{ expensesCount }} {{ $t('finance.transactions') }}
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-header">
            <span class="stat-label">{{ $t('finance.transactionHistory') }}</span>
            <div class="stat-icon blue">
              <i class="fas fa-exchange-alt"></i>
            </div>
          </div>
          <div class="stat-value">{{ transactions.length }}</div>
          <div class="stat-change">
            <i class="fas fa-list"></i>
            {{ $t('finance.allTransactions') }}
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-header">
            <span class="stat-label">{{ $t('finance.pendingPayments') }}</span>
            <div class="stat-icon purple">
              <i class="fas fa-clock"></i>
            </div>
          </div>
          <div class="stat-value">{{ pendingPayments.length }}</div>
          <div class="stat-change">
            <i class="fas fa-hourglass-half"></i>
            {{ $t('finance.toValidate') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Transactions Section -->
    <div class="transactions-section">
      <div class="section-header">
        <div class="section-title">
          {{ 
            activeTab === 'history' ? $t('finance.transactionHistory') : 
            activeTab === 'pending' ? $t('finance.pendingPayments') :
            $t('provisioning.requests')
          }}
          <span class="badge">{{ 
            activeTab === 'history' ? transactions.length : 
            activeTab === 'pending' ? pendingPayments.length :
            provisioningRequests.length
          }}</span>
        </div>
        <div class="tab-buttons">
          <button 
            class="tab-button" 
            :class="{ active: activeTab === 'history' }"
            @click="activeTab = 'history'"
          >
            {{ $t('finance.transactionHistory') }}
          </button>
          <button 
            class="tab-button" 
            :class="{ active: activeTab === 'pending' }"
            @click="activeTab = 'pending'"
          >
            {{ $t('finance.pendingPayments') }}
            <span v-if="pendingPayments.length > 0" class="pending-count">{{ pendingPayments.length }}</span>
          </button>
          <button 
            class="tab-button" 
            :class="{ active: activeTab === 'provisioning' }"
            @click="activeTab = 'provisioning'"
          >
            {{ $t('provisioning.requests') }}
            <span v-if="pendingProvisioningCount > 0" class="pending-count">{{ pendingProvisioningCount }}</span>
          </button>
        </div>
      </div>

      <!-- Transaction History Tab Content -->
      <div v-show="activeTab === 'history'" class="tab-content">
        <AppDataGrid
          ref="transactionsGrid"
          class="transactions-grid"
          :columnDefs="transactionColumns"
          :rowData="transactions"
          :defaultColDef="defaultColDef"
          :actions="transactionActions"
          :pagination="true"
          :pageSize="20"
          :pageSizeOptions="[20, 30, 50, 100]"
          :suppressPaginationPanel="false"
          :animateRows="true"
          :showSearch="true"
          :searchPlaceholder="$t('finance.searchTransactions')"
          @grid-ready="onTransactionGridReady"
          @action-clicked="handleTransactionAction"
          gridStyle="width: 100%;"
          domLayout="autoHeight"
        />
      </div>

      <!-- Pending Payments Tab Content -->
      <div v-show="activeTab === 'pending'" class="tab-content">
        <AppDataGrid
          ref="pendingPaymentsGrid"
          class="pending-payments-grid"
          :columnDefs="pendingPaymentsColumns"
          :rowData="pendingPayments"
          :defaultColDef="defaultColDef"
          :actions="pendingActions"
          :pagination="true"
          :pageSize="10"
          :pageSizeOptions="[10, 25, 50]"
          :suppressPaginationPanel="false"
          :animateRows="true"
          :showSearch="true"
          :searchPlaceholder="$t('finance.searchPayments')"
          @grid-ready="onPendingGridReady"
          @action-clicked="handlePendingAction"
          gridStyle="width: 100%;"
          domLayout="autoHeight"
        />
      </div>
      
      <!-- Provisioning Requests Tab Content -->
      <div v-show="activeTab === 'provisioning'" class="tab-content">
        <div v-if="provisioningRequests.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-hand-holding-usd"></i>
          </div>
          <h3>{{ $t('provisioning.noRequests') }}</h3>
          <p>{{ $t('provisioning.noRequestsDescription') }}</p>
        </div>
        <AppDataGrid
          v-else
          ref="provisioningRequestsGrid"
          class="provisioning-requests-grid"
          :columnDefs="provisioningRequestColumns"
          :rowData="provisioningRequests"
          :defaultColDef="defaultColDef"
          :actions="provisioningActions"
          :pagination="true"
          :pageSize="10"
          :pageSizeOptions="[10, 25, 50]"
          :suppressPaginationPanel="false"
          :animateRows="true"
          :showSearch="true"
          :searchPlaceholder="$t('provisioning.searchRequests')"
          @grid-ready="onProvisioningGridReady"
          @action-clicked="handleProvisioningAction"
          gridStyle="width: 100%;"
          domLayout="autoHeight"
        />
      </div>
    </div>
      <!-- Record Payment Modal -->
    <RecordPaymentModal
      :isVisible="showRecordPaymentModal"
      :provisioningRequest="provisioningRequestToApprove"
      @cancel="showRecordPaymentModal = false; provisioningRequestToApprove = null"
      @submit="handlePaymentSubmit"
    />    <!-- Record Expense Modal -->
    <RecordExpenseModal
      :isVisible="showRecordExpenseModal"
      :tasks="tasks"
      :intervenants="intervenants"
      @cancel="showRecordExpenseModal = false"
      @submit="handleExpenseSubmit"
    />
      <!-- Edit Payment Modal -->
    <RecordPaymentModal
      :isVisible="showEditPaymentModal"
      :isEditMode="true"
      :payment="editingPayment"
      @cancel="showEditPaymentModal = false"
      @submit="handleEditPaymentSubmit"
    />
      <!-- Cancel Payment Confirmation Modal -->
    <ConfirmModal
      :isVisible="showCancelPaymentModal"
      :title="$t('finance.cancelPayment')"
      :message="$t('finance.confirmCancelPaymentMessage')"
      :details="$t('finance.confirmCancelPaymentDetails')"
      :confirmText="$t('finance.cancelPayment')"
      :cancelText="$t('common.cancel')"
      type="danger"
      @cancel="cancelCancelPayment"
      @confirm="confirmCancelPayment"
    />
    
    <!-- Edit Expense Modal -->
    <RecordExpenseModal
      :isVisible="showEditExpenseModal"
      :isEditMode="true"
      :expense="editingExpense"
      :tasks="tasks"
      :intervenants="intervenants"
      @cancel="showEditExpenseModal = false"
      @submit="handleEditExpenseSubmit"
    />
    
    <!-- Cancel Transaction Confirmation Modal -->
    <ConfirmModal
      :isVisible="showCancelTransactionModal"
      :title="$t('finance.cancelTransaction')"
      :message="$t('finance.confirmCancelTransactionMessage')"
      :details="$t('finance.confirmCancelTransactionDetails')"
      :confirmText="$t('finance.cancelTransaction')"
      :cancelText="$t('common.cancel')"
      type="danger"
      @cancel="cancelCancelTransaction"
      @confirm="confirmCancelTransaction"
    />
    
    <!-- Provisioning Request Modal -->
    <ProvisioningRequestModal
      :isVisible="showProvisioningModal"
      @cancel="showProvisioningModal = false"
      @submit="handleProvisioningSubmit"
    />
    
    <!-- Reject Provisioning Request Modal -->
    <AppModal
      :show="showRejectProvisioningModal"
      :title="$t('provisioning.rejectRequest')"
      size="medium"
      @close="cancelRejectProvisioning"
    >
      <div class="reject-provisioning-modal">
        <!-- Warning Icon and Message -->
        <div class="reject-provisioning-modal__header">
          <div class="reject-provisioning-modal__icon">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path fill="currentColor" d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"/>
            </svg>
          </div>
          <div class="reject-provisioning-modal__text">
            <p class="reject-provisioning-modal__message">{{ $t('provisioning.confirmRejectMessage') }}</p>
          </div>
        </div>
        
        <!-- Details -->
        <div class="reject-provisioning-modal__details">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
          </svg>
          <div class="reject-provisioning-modal__details-text">
            {{ $t('provisioning.confirmRejectDetails') }}
          </div>
        </div>
        
        <!-- Rejection Notes Textarea -->
        <div class="reject-provisioning-modal__notes">
          <label for="rejectionNotes" class="reject-provisioning-modal__label">
            {{ $t('provisioning.rejectionNotes') }}
          </label>
          <textarea
            id="rejectionNotes"
            v-model="rejectionNotes"
            class="reject-provisioning-modal__textarea"
            :placeholder="$t('provisioning.rejectionNotesPlaceholder')"
            rows="4"
          ></textarea>
        </div>
      </div>
      
      <template #footer>
        <div class="reject-provisioning-modal__actions">
          <AppButton 
            type="button"
            variant="outline" 
            @click="cancelRejectProvisioning"
          >
            {{ $t('common.cancel') }}
          </AppButton>
          <AppButton 
            type="button"
            variant="danger"
            @click="confirmRejectProvisioning"
          >
            {{ $t('provisioning.reject') }}
          </AppButton>
        </div>
      </template>
    </AppModal>
    
    <!-- Document Viewer Modal -->
    <teleport to="body">
      <transition name="fade">
        <DocumentViewerModal
          v-if="showDocumentViewerModal && selectedDocument"
          :projectId="projectId"
          :document="selectedDocument"
          @close="showDocumentViewerModal = false"
        />
      </transition>
    </teleport>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/composables/useStore';
import { usePermissions } from '@/composables/usePermissions';
import { useNotifications } from '@/composables/useNotifications';
import AppButton from '@/components/base/AppButton.vue';
import AppDataGrid from '@/components/base/AppDataGrid.vue';
import AppModal from '@/components/base/Modal.vue';
import financeService from '@/services/finance.service';
import DocumentService from '@/services/documents.service';
import DocumentViewerModal from '../documents/DocumentViewerModal.vue';
import RecordPaymentModal from './RecordPaymentModal.vue';
import RecordExpenseModal from './RecordExpenseModal.vue';
import ProvisioningRequestModal from './ProvisioningRequestModal.vue';
import ConfirmModal from '@/components/common/ConfirmModal.vue';
import PermissionGuard from '@/components/common/PermissionGuard.vue';
import projectTasksService from '@/services/projectTasks.service';
import intervenantsService from '@/services/intervenants.service';
import { provisioningRequestService } from '@/services/provisioningRequest.service';
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils';
import { PermissionType } from '@/services/permission.service';
import type { ProvisioningRequestData } from '@/types/services';

interface Props {
  projectId: string | number;
}

const props = defineProps<Props>();

const { t } = useI18n();
const store = useStore();
const { hasPermissionForProject } = usePermissions();
const { notifySuccess, notifyError } = useNotifications();

// State
const activeTab = ref('history');
const projectBalance = ref(0);
const formattedBalance = ref('€0.00');
const transactions = ref([]);
const pendingPayments = ref([]);
const formattedPendingAmounts = ref({});
const formattedTransactionAmounts = ref({});
const showRecordPaymentModal = ref(false);
const showRecordExpenseModal = ref(false);
const showProvisioningModal = ref(false);

// Provisioning request to approve (pre-fill payment modal)
const provisioningRequestToApprove = ref(null);

// Provisioning requests state
const provisioningRequests = ref([]);
const formattedProvisioningAmounts = ref({});
const pendingProvisioningCount = ref(0);
const showProvisioningResponseModal = ref(false);
const provisioningRequestToRespond = ref(null);
const provisioningResponseType = ref<'approve' | 'reject'>('approve');

// Provisioning reject confirmation modal
const showRejectProvisioningModal = ref(false);
const provisioningRequestToReject = ref(null);
const rejectionNotes = ref('');

// Confirmation modal state
const showCancelPaymentModal = ref(false);
const paymentToCancel = ref(null);
// Document viewer state
const showDocumentViewerModal = ref(false);
const selectedDocument = ref(null);

// Tasks list state
const tasks = ref([]);
const loadingTasks = ref(false);

// Intervenants list state
const intervenants = ref([]);
const loadingIntervenants = ref(false);

// Add watchers to track modal visibility

watch(showRecordPaymentModal, (newVal: boolean) => {
  console.log('Payment modal visibility changed to:', newVal);
});

watch(showRecordExpenseModal, (newVal: boolean) => {
  console.log('Expense modal visibility changed to:', newVal);
});

// Grid state
const pendingPaymentsGridApi = ref(null);
const transactionsGridApi = ref(null);

//  Edit payment
const showEditPaymentModal = ref(false);
const editingPayment = ref(null);

//  Edit expense
const showEditExpenseModal = ref(false);
const editingExpense = ref(null);

// Cancel transaction
const showCancelTransactionModal = ref(false);
const transactionToCancel = ref(null);

// Grid configuration
const defaultColDef = {
  sortable: true,
  resizable: true
};

// Column definitions for pending payments grid - make reactive to language changes
const pendingPaymentsColumns = computed(() => [
  {
    field: 'transactionDate',
    headerName: t('finance.date'),
    type: 'date'
  },
  {
    field: 'amount',
    headerName: t('finance.amount'),
    type: 'currency',
    cellRenderer: (params: any) => {
      const formattedAmount = formattedPendingAmounts.value[params.data.id] || '...';
      return `<span class="currency-amount">${formattedAmount}</span>`;
    }
  },
  {
    field: 'description',
    headerName: t('finance.description'),
    type: 'text'
  }
]);

// Actions definitions for pending payments grid - make reactive to language changes
const pendingActions = computed(() => {
  const actions = [];
  
  // Edit action (no specific permission check needed for payment editing)
  actions.push({
    name: 'edit',
    icon: 'fas fa-edit',
    label: t('common.edit'),
    tooltip: t('common.edit'),
    class: 'edit-btn'
  });
  
  // Validate action - check validate payment permission
  if (hasPermissionForProject(PermissionType.VALIDATE_PAYMENT, props.projectId).value) {
    actions.push({
      name: 'validate',
      icon: 'fas fa-check-circle',
      label: t('finance.validate'),
      tooltip: t('finance.validate'),
      class: 'validate-btn'
    });
  }
  
  // Cancel action (no specific permission check needed for payment cancellation)
  actions.push({
    name: 'cancel',
    icon: 'fas fa-times-circle',
    label: t('common.cancel'),
    tooltip: t('finance.cancelPayment'),
    class: 'cancel-btn'
  });
  
  // View and download actions (always available)
  actions.push({
    name: 'view',
    icon: 'fas fa-eye',
    label: t('documents.view'),
    tooltip: t('documents.view'),
    class: 'view-btn',
    condition: (data: any) => !!data.documentId
  });
  
  actions.push({
    name: 'download',
    icon: 'fas fa-download',
    label: t('documents.download'),
    tooltip: t('documents.download'),
    class: 'download-btn',
    condition: (data: any) => !!data.documentId
  });
  
  return actions;
});

const transactionColumns = computed(() => [
  {
    field: 'transactionDate',
    headerName: t('finance.transactionDate'),
    type: 'date',
    width: 110
  },
  {
    field: 'type',
    headerName: t('finance.type'),
    cellRenderer: (params: any) => {
      const type = params.value;
      const badgeClass = type === 'PAYMENT' ? 'badge-success' : 'badge-danger';
      const icon = type === 'PAYMENT' ? 'fa-arrow-down' : 'fa-arrow-up';
      return `
        <span class="type-badge ${badgeClass}">
          <i class="fas ${icon}"></i>
          ${t(`finance.${type.toLowerCase()}`)}
        </span>
      `;
    },
    type: 'text',
    width: 130
  },
  {
    field: 'amount',
    headerName: t('finance.amount'),
    type: 'currency',
    cellClass: (params: any) => params.data.type === 'EXPENSE' ? 'negative-amount' : 'positive-amount',
    width: 110,
    cellRenderer: (params: any) => {
      const formattedAmount = formattedTransactionAmounts.value[params.data.id] || '...';
      const cssClass = params.data.type === 'EXPENSE' ? 'negative-amount' : 'positive-amount';
      return `<span class="currency-amount ${cssClass}">${formattedAmount}</span>`;
    }
  },
  {
    field: 'taskFullName',
    headerName: t('finance.task'),
    type: 'text'
  },
  {
    field: 'status',
    headerName: t('finance.status'),
    cellRenderer: (params: any) => `
      <span class="status-badge status-${params.value.toLowerCase()}">
        ${t(`finance.statusOptions.${params.value.toLowerCase()}`)}
      </span>
    `,
    type: 'text',
    width: 100
  }
]);

// Actions for transactions grid - make reactive to language changes
const transactionActions = computed(() => {
  const actions = [];
  
  // Edit and cancel actions for expenses - check manage expense permission
  if (hasPermissionForProject(PermissionType.MANAGE_EXPENSE, props.projectId).value) {
    actions.push({
      name: 'edit',
      icon: 'fas fa-edit',
      label: t('common.edit'),
      tooltip: t('common.edit'),
      class: 'edit-btn',
      condition: (data: any) => data.type === 'EXPENSE'
    });
    
    actions.push({
      name: 'cancel',
      icon: 'fas fa-times-circle',
      label: t('common.cancel'),
      tooltip: t('finance.cancelTransaction'),
      class: 'cancel-btn',
      condition: (data: any) => data.type === 'EXPENSE'
    });
  }
  
  // View and download actions (always available)
  actions.push({
    name: 'view',
    icon: 'fas fa-eye',
    label: t('documents.view'),
    tooltip: t('documents.view'),
    class: 'view-btn',
    condition: (data: any) => !!data.documentId
  });
  
  actions.push({
    name: 'download',
    icon: 'fas fa-download',
    label: t('documents.download'),
    tooltip: t('documents.download'),
    class: 'download-btn',
    condition: (data: any) => !!data.documentId
  });
  
  return actions;
});

// Provisioning requests columns
const provisioningRequestColumns = computed(() => [
  {
    field: 'createdAt',
    headerName: t('provisioning.requestDate'),
    type: 'date',
    width: 110
  },
  {
    field: 'createdByName',
    headerName: t('provisioning.requestedBy'),
    type: 'text',
    width: 150
  },
  {
    field: 'requestedAmount',
    headerName: t('provisioning.amount'),
    type: 'currency',
    cellClass: 'positive-amount',
    width: 120,
    cellRenderer: (params: any) => {
      const formattedAmount = formattedProvisioningAmounts.value[params.data.id] || '...';
      return `<span class="currency-amount positive-amount">${formattedAmount}</span>`;
    }
  },
  {
    field: 'provisionDeadline',
    headerName: t('provisioning.deadline'),
    type: 'date',
    width: 110,
    cellRenderer: (params: any) => {
      const deadline = new Date(params.value);
      const today = new Date();
      const isOverdue = deadline < today;
      const cssClass = isOverdue ? 'text-danger' : '';
      return `<span class="${cssClass}">${params.value}</span>`;
    }
  },
  {
    field: 'description',
    headerName: t('provisioning.description'),
    type: 'text',
    flex: 1
  },
  {
    field: 'status',
    headerName: t('provisioning.status'),
    cellRenderer: (params: any) => {
      const statusMap: Record<string, { class: string; icon: string }> = {
        'PENDING': { class: 'status-pending', icon: 'fa-clock' },
        'APPROVED': { class: 'status-approved', icon: 'fa-check-circle' },
        'REJECTED': { class: 'status-rejected', icon: 'fa-times-circle' }
      };
      const status = statusMap[params.value] || statusMap['PENDING'];
      return `
        <span class="status-badge ${status.class}">
          <i class="fas ${status.icon}"></i>
          ${t(`provisioning.statusOptions.${params.value.toLowerCase()}`)}
        </span>
      `;
    },
    type: 'text',
    width: 120
  },
  {
    field: 'respondedByName',
    headerName: t('provisioning.respondedBy'),
    type: 'text',
    width: 150,
    cellRenderer: (params: any) => params.value || '-'
  }
]);

// Actions for provisioning requests grid
const provisioningActions = computed(() => {
  const actions = [];
  
  // Approve and Reject actions - only for PENDING requests and client users
  if (hasPermissionForProject(PermissionType.ANSWER_FINANCE_PROVISIONING, props.projectId).value) {
    actions.push({
      name: 'approve',
      icon: 'fas fa-check',
      label: t('provisioning.approve'),
      tooltip: t('provisioning.approve'),
      class: 'approve-btn',
      condition: (data: any) => data.status === 'PENDING'
    });
    
    actions.push({
      name: 'reject',
      icon: 'fas fa-times',
      label: t('provisioning.reject'),
      tooltip: t('provisioning.reject'),
      class: 'reject-btn',
      condition: (data: any) => data.status === 'PENDING'
    });
  }
  
  return actions;
});

// Methods
const formatPendingAmounts = async () => {
  if (!pendingPayments.value?.length) return;
  
  const amounts: Record<string, string> = {};
  for (const payment of pendingPayments.value) {
    if (payment.id) {
      try {
        amounts[payment.id] = await formatCurrencyWithUserPreference(payment.amount);
      } catch (error) {
        console.warn('Error formatting currency for payment:', payment.id, error);
        amounts[payment.id] = payment.amount?.toString() || '0';
      }
    }
  }
  
  formattedPendingAmounts.value = amounts;
};

const formatTransactionAmounts = async () => {
  if (!transactions.value?.length) return;
  
  const amounts: Record<string, string> = {};
  for (const transaction of transactions.value) {
    if (transaction.id) {
      try {
        amounts[transaction.id] = await formatCurrencyWithUserPreference(transaction.amount);
      } catch (error) {
        console.warn('Error formatting currency for transaction:', transaction.id, error);
        amounts[transaction.id] = transaction.amount?.toString() || '0';
      }
    }
  }
  
  formattedTransactionAmounts.value = amounts;
};

const formatProvisioningRequestAmounts = async () => {
  if (!provisioningRequests.value?.length) return;
  
  const amounts: Record<string, string> = {};
  for (const request of provisioningRequests.value) {
    if (request.id) {
      try {
        amounts[request.id] = await formatCurrencyWithUserPreference(request.requestedAmount);
      } catch (error) {
        console.warn('Error formatting currency for provisioning request:', request.id, error);
        amounts[request.id] = request.requestedAmount?.toString() || '0';
      }
    }
  }
  
  formattedProvisioningAmounts.value = amounts;
};

const loadData = async () => {
  console.log('🔄 Loading finance data for project:', props.projectId);
  try {
    
    const [balanceData, transactionsData, pendingData, provisioningData] = await Promise.all([
        financeService.getBalance(props.projectId),
        financeService.getTransactions(props.projectId),
        financeService.getPendingPayments(props.projectId),
        provisioningRequestService.getRequests(props.projectId)
    ]);

    console.log('📊 Balance data:', balanceData);
    console.log('📊 Transactions data:', transactionsData);
    console.log('📊 Pending payments data:', pendingData);
    console.log('📊 Provisioning requests data:', provisioningData);

    projectBalance.value = balanceData?.currentBalance || 0;
    transactions.value = transactionsData || [];
    pendingPayments.value = pendingData?.content || [];
    provisioningRequests.value = provisioningData || [];
    pendingProvisioningCount.value = (provisioningData || []).filter((r: any) => r.status === 'PENDING').length;
    
    console.log('💰 Project balance:', projectBalance.value);
    console.log('📋 Transactions count:', transactions.value.length);
    console.log('⏳ Pending payments count:', pendingPayments.value.length);
    console.log('🤝 Provisioning requests count:', provisioningRequests.value.length);
    console.log('⏰ Pending provisioning requests:', pendingProvisioningCount.value);
    
    // Format currency amounts after loading data
    await Promise.all([
      formatPendingAmounts(),
      formatTransactionAmounts(),
      formatProvisioningRequestAmounts()
    ]);
    
    // Refresh grids to show formatted amounts
    setTimeout(() => {
      pendingPaymentsGridApi.value?.refreshCells();
      transactionsGridApi.value?.refreshCells();
      provisioningRequestsGridApi.value?.refreshCells();
    }, 100);
  } catch (error) {
    console.error('❌ Error loading finance data:', error);
    store.dispatch('notifications/showError', t('finance.errorLoading'));
  }
};

const loadTasks = async () => {
  try {
    loadingTasks.value = true;
    const response = await projectTasksService.getTasks(props.projectId);
    tasks.value = response || [];
  } catch (error) {
    console.error('Error loading tasks:', error);
    store.dispatch('notifications/showError', t('finance.errorLoadingTasks'));
  } finally {
    loadingTasks.value = false;
  }
};

const loadIntervenants = async () => {
  try {
    loadingIntervenants.value = true;
    intervenants.value = await intervenantsService.getIntervenants(props.projectId);
  } catch (error) {
    console.error('Error loading intervenants:', error);
    store.dispatch('notifications/showError', t('finance.errorLoadingIntervenants'));
  } finally {
    loadingIntervenants.value = false;
  }
};

const handlePaymentSubmit = async ({ data, isValid }: { data: any; isValid: boolean }) => {
  if (!isValid) {
    store.dispatch('notifications/showError', t('finance.fillRequiredFields'));
    return;
  }
  
  const isProvisioningApproval = !!provisioningRequestToApprove.value;
  
  try {
    // Submit the payment (backend will approve the provisioning request automatically if linked)
    await financeService.recordPayment(props.projectId, data);
    
    // Refresh data and close modal
    await loadData();
    showRecordPaymentModal.value = false;
    provisioningRequestToApprove.value = null;
    
    if (isProvisioningApproval) {
      store.dispatch('notifications/showSuccess', t('provisioning.approvedAndPaymentRecorded'));
    } else {
      store.dispatch('notifications/showSuccess', t('finance.paymentRecorded'));
    }
  } catch (error) {
    console.error('Error recording payment:', error);
    store.dispatch('notifications/showError', t('finance.errorRecordingPayment'));
  }
};const handleExpenseSubmit = async ({ data, isValid }: { data: any; isValid: boolean }) => {
  if (!isValid) {
    store.dispatch('notifications/showError', t('finance.fillRequiredFields'));
    return;
  }
  
  try {
    // Prepare expense data
    const expenseData = {
      ...data,
      taskId: data.taskId || null // Allow null taskId if not selected
    };
    
    // Submit the expense
    await financeService.recordExpense(props.projectId, expenseData);
    
    // Refresh data and close modal
    await loadData();
    showRecordExpenseModal.value = false;
    store.dispatch('notifications/showSuccess', t('finance.expenseRecorded'));
  } catch (error) {
    console.error('Error recording expense:', error);
    store.dispatch('notifications/showError', t('finance.errorRecordingExpense'));
  }
};

const validatePayment = async (transactionId: string) => {
  try {
    await financeService.validatePayment(props.projectId, transactionId);
    await loadData();
    store.dispatch('notifications/showSuccess', t('finance.paymentValidated'));
  } catch (error) {
    console.error('Error validating payment:', error);
    store.dispatch('notifications/showError', t('finance.errorValidatingPayment'));
  }
};

const editPayment = (payment: any) => {
  editingPayment.value = payment;
  showEditPaymentModal.value = true;
};

const handleEditPaymentSubmit = async ({ data, isValid }: { data: any; isValid: boolean }) => {
  if (!isValid) {
    store.dispatch('notifications/showError', t('finance.fillRequiredFields'));
    return;
  }
  
  try {
    // Submit the edited payment
    await financeService.editPendingPayment(props.projectId, editingPayment.value.id, data);
    
    // Refresh data and close modal
    await loadData();
    showEditPaymentModal.value = false;
    editingPayment.value = null;
    store.dispatch('notifications/showSuccess', t('finance.paymentUpdated'));
  } catch (error) {
    console.error('Error updating payment:', error);
    store.dispatch('notifications/showError', t('finance.errorUpdatingPayment'));
  }
};

const cancelPayment = (transactionId: string) => {
  // Store the transaction ID and show confirmation modal
  paymentToCancel.value = transactionId;
  showCancelPaymentModal.value = true;
};

const confirmCancelPayment = async () => {
  if (!paymentToCancel.value) return;
  
  try {
    await financeService.cancelPendingPayment(props.projectId, paymentToCancel.value);
    await loadData();
    store.dispatch('notifications/showSuccess', t('finance.paymentCancelled'));
  } catch (error) {
    console.error('Error cancelling payment:', error);
    store.dispatch('notifications/showError', t('finance.errorCancellingPayment'));
  } finally {
    // Reset state
    showCancelPaymentModal.value = false;
    paymentToCancel.value = null;
  }
};

const cancelCancelPayment = () => {
  showCancelPaymentModal.value = false;
  paymentToCancel.value = null;
};

const openPaymentModal = () => {
  console.log('Opening payment modal');
  showRecordPaymentModal.value = true;
};

const openExpenseModal = async () => {
  console.log('Opening expense modal');
  await Promise.all([loadTasks(), loadIntervenants()]);
  showRecordExpenseModal.value = true;
};

const openProvisioningModal = () => {
  console.log('Opening provisioning modal');
  showProvisioningModal.value = true;
};

const handleProvisioningSubmit = async ({ data, isValid }: { data: ProvisioningRequestData; isValid: boolean }) => {
  if (!isValid) {
    notifyError(t('finance.fillRequiredFields'));
    return;
  }
  
  try {
    await provisioningRequestService.createRequest(props.projectId as number, data);
    
    // Close modal
    showProvisioningModal.value = false;
    
    // Show success notification
    notifySuccess(t('provisioning.requestSubmitted'));
    
    // Optionally refresh data to show updated pending requests count
    await loadData();
  } catch (error) {
    console.error('Error submitting provisioning request:', error);
    notifyError(t('provisioning.errorSubmitting'));
  }
};

// Edit expense
const editExpense = async (expense: any) => {
  await Promise.all([loadTasks(), loadIntervenants()]);
  editingExpense.value = expense;
  showEditExpenseModal.value = true;
};

const handleEditExpenseSubmit = async ({ data, isValid }: { data: any; isValid: boolean }) => {
  if (!isValid) {
    store.dispatch('notifications/showError', t('finance.fillRequiredFields'));
    return;
  }
  
  try {
    // Submit the edited expense
    await financeService.editExpense(props.projectId, editingExpense.value.id, data);
    
    // Refresh data and close modal
    await loadData();
    showEditExpenseModal.value = false;
    editingExpense.value = null;
    store.dispatch('notifications/showSuccess', t('finance.expenseUpdated'));
  } catch (error) {
    console.error('Error updating expense:', error);
    store.dispatch('notifications/showError', t('finance.errorUpdatingExpense'));
  }
};

// Cancel transaction
const cancelTransaction = (transactionId: string) => {
  // Store the transaction ID and show confirmation modal
  transactionToCancel.value = transactionId;
  showCancelTransactionModal.value = true;
};

const confirmCancelTransaction = async () => {
  if (!transactionToCancel.value) return;
  
  try {
    await financeService.cancelTransaction(props.projectId, transactionToCancel.value);
    await loadData();
    store.dispatch('notifications/showSuccess', t('finance.transactionCancelled'));
  } catch (error) {
    console.error('Error cancelling transaction:', error);
    store.dispatch('notifications/showError', t('finance.errorCancellingTransaction'));
  } finally {
    // Reset state
    showCancelTransactionModal.value = false;
    transactionToCancel.value = null;
  }
};

const cancelCancelTransaction = () => {
  showCancelTransactionModal.value = false;
  transactionToCancel.value = null;
};

// Invoice viewing and downloading
const viewInvoice = (transaction: any) => {
  if (!transaction?.documentId) {
    store.dispatch('notifications/showError', t('errors.noInvoiceAttached'));
    return;
  }
  
  // Convert transaction to document format for the document viewer
  const document = transactionToDocumentFormat(transaction);
  selectedDocument.value = document;
  showDocumentViewerModal.value = true;
};

const downloadInvoice = async (transaction: any) => {
  if (!props.projectId || !transaction?.documentId) {
    store.dispatch('notifications/showError', t('errors.invalidInvoiceDownload'));
    return;
  }

  try {
    await DocumentService.downloadDocument(
      props.projectId, 
      transaction.documentId,
      transaction.invoiceFileName || `invoice-${transaction.id}.pdf`
    );
  } catch (err) {
    console.error('Error downloading invoice:', err);
    store.dispatch('notifications.showError', t('errors.downloadingInvoiceError'));
  }
};

// Helper function to convert a transaction to document format
const transactionToDocumentFormat = (transaction: any) => {
  return {
    id: transaction.documentId,
    fileName: transaction.invoiceFileName || `invoice-${transaction.id}.pdf`,
    fileType: guessFileTypeFromName(transaction.invoiceFileName || 'invoice.pdf'),
    docType: 'recu', // Default document type for invoices
    description: transaction.description || t('finance.invoice'),
    createdAt: transaction.createdAt
  };
};

// Helper to guess file type from filename
const guessFileTypeFromName = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  const mimeTypes: Record<string, string> = {
    'pdf': 'application/pdf',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif'
  };
  
  return mimeTypes[extension || ''] || 'application/octet-stream';
};

// Update formatted balance when projectBalance changes
watch(projectBalance, async (newBalance: number) => {
  formattedBalance.value = await formatCurrencyWithUserPreference(newBalance);
}, { immediate: true });

// Grid ready event handlers
const onPendingGridReady = (params: any) => {
  pendingPaymentsGridApi.value = params.api;
  setTimeout(() => {
    pendingPaymentsGridApi.value.sizeColumnsToFit();
  }, 0);
};

const onTransactionGridReady = (params: any) => {
  transactionsGridApi.value = params.api;
  setTimeout(() => {
    transactionsGridApi.value.sizeColumnsToFit();
  }, 0);
};

const provisioningRequestsGridApi = ref(null);

const onProvisioningGridReady = (params: any) => {
  provisioningRequestsGridApi.value = params.api;
  setTimeout(() => {
    provisioningRequestsGridApi.value.sizeColumnsToFit();
  }, 0);
};

// Define a resize handler function
const handleResize = () => {
  setTimeout(() => {
    pendingPaymentsGridApi.value?.sizeColumnsToFit();
    transactionsGridApi.value?.sizeColumnsToFit();
    provisioningRequestsGridApi.value?.sizeColumnsToFit();
  }, 100);
};

// Action handlers for AppDataGrid components
const handlePendingAction = (action: any, data: any) => {
  if (!data) return;
  
  switch (action.name) {
    case 'edit':
      editPayment(data);
      break;
    case 'validate':
      validatePayment(data.id);
      break;    case 'cancel':
      cancelPayment(data.id);
      break;
    case 'view':
      viewInvoice(data);
      break;
    case 'download':
      downloadInvoice(data);
      break;
    default:
      console.warn('Unknown action:', action.name);
  }
};

const handleTransactionAction = (action: any, data: any) => {
  if (!data) return;
  
  switch (action.name) {
    case 'edit':
      editExpense(data);
      break;
    case 'cancel':
      cancelTransaction(data.id);
      break;
    case 'view':
      viewInvoice(data);
      break;
    case 'download':
      downloadInvoice(data);
      break;
    default:
      console.warn('Unknown action:', action.name);
  }
};

const handleProvisioningAction = (action: any, data: any) => {
  if (!data) return;
  
  switch (action.name) {
    case 'approve':
      approveProvisioningRequest(data);
      break;
    case 'reject':
      rejectProvisioningRequest(data);
      break;
    default:
      console.warn('Unknown provisioning action:', action.name);
  }
};

// Provisioning request methods
const approveProvisioningRequest = async (request: any) => {
  // Set the provisioning request to approve and open payment modal
  provisioningRequestToApprove.value = request;
  showRecordPaymentModal.value = true;
};

const rejectProvisioningRequest = (request: any) => {
  // Reset notes and store the request
  rejectionNotes.value = '';
  provisioningRequestToReject.value = request;
  showRejectProvisioningModal.value = true;
};

const confirmRejectProvisioning = async () => {
  if (!provisioningRequestToReject.value) return;
  
  try {
    await provisioningRequestService.rejectRequest(
      props.projectId,
      provisioningRequestToReject.value.id,
      { responseNotes: rejectionNotes.value || '' }
    );
    
    notifySuccess(t('provisioning.rejected'));
    await loadData(); // Reload all data
  } catch (error) {
    console.error('Error rejecting provisioning request:', error);
    notifyError(t('provisioning.rejectFailed'));
  } finally {
    // Reset state
    showRejectProvisioningModal.value = false;
    provisioningRequestToReject.value = null;
    rejectionNotes.value = '';
  }
};

const cancelRejectProvisioning = () => {
  showRejectProvisioningModal.value = false;
  provisioningRequestToReject.value = null;
  rejectionNotes.value = '';
};

// Computed properties for stats
const totalPayments = computed(() => {
  return transactions.value
    .filter((t: any) => t.type === 'PAYMENT')
    .reduce((sum: number, t: any) => sum + (t.amount || 0), 0);
});

const totalExpenses = computed(() => {
  return transactions.value
    .filter((t: any) => t.type === 'EXPENSE')
    .reduce((sum: number, t: any) => sum + (t.amount || 0), 0);
});

const paymentsCount = computed(() => {
  return transactions.value.filter((t: any) => t.type === 'PAYMENT').length;
});

const expensesCount = computed(() => {
  return transactions.value.filter((t: any) => t.type === 'EXPENSE').length;
});

// Formatted currency values
const formattedTotalPayments = ref('€0.00');
const formattedTotalExpenses = ref('€0.00');

// Watch for changes in totals and format them
watch(totalPayments, async (newValue: number) => {
  formattedTotalPayments.value = await formatCurrencyWithUserPreference(newValue);
}, { immediate: true });

watch(totalExpenses, async (newValue: number) => {
  formattedTotalExpenses.value = await formatCurrencyWithUserPreference(newValue);
}, { immediate: true });

const formatCurrency = async (value: number) => {
  return await formatCurrencyWithUserPreference(value);
};

// Component lifecycle
onMounted(async () => {
  console.log('🏗️ ProjectFinance component mounted with projectId:', props.projectId);
  
  // Initialize formatted balance
  formattedBalance.value = await formatCurrencyWithUserPreference(projectBalance.value);
  
  loadData();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.project-finance {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem;
}

/* Main Content Grid */
.finance-content-grid {
  display: grid;
  grid-template-columns: 1fr 700px;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

/* Modern Balance Card */
.balance-card-modern {
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.25);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.balance-card-modern:hover {
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.35);
  transform: translateY(-2px);
}

.balance-card-background {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.balance-card-background::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.balance-card-content {
  position: relative;
  z-index: 1;
  padding: 1.5rem 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
}

.balance-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
}

.balance-amount {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 2rem;
  line-height: 1;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.balance-amount.negative {
  color: #fee2e2;
}

.balance-actions {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  justify-content: center;
}

.btn {
  width: auto;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.btn-primary {
  background: white;
  color: #667eea;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Stats Grid - Single Row Layout */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 1rem;
}

.stat-card {
  background: white;
  border-radius: 1rem;
  padding: 1.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.stat-label {
  font-size: 0.6875rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}

.stat-icon.green {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.stat-icon.red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.stat-icon.blue {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.stat-icon.purple {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.375rem;
  line-height: 1;
}

.stat-change {
  font-size: 0.6875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #64748b;
  font-weight: 500;
}

.stat-change.positive {
  color: #10b981;
}

.stat-change.negative {
  color: #ef4444;
}

/* Transactions Section */
.transactions-section {
  background: white;
  border-radius: 1.25rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
}

/* Tab Buttons - Match Demo Exactly */
.tab-buttons {
  display: flex;
  gap: 0.5rem;
  background: #f8fafc;
  padding: 0.25rem;
  border-radius: 0.625rem;
}

.tab-button {
  padding: 0.5rem 1rem;
  border: none;
  background: transparent;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.tab-button:hover {
  color: #475569;
}

.tab-button.active {
  background: white;
  color: #667eea;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pending-count {
  background: #ef4444;
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 0.625rem;
  font-size: 0.6875rem;
  font-weight: 700;
  margin-left: 0.25rem;
}

.tab-content {
  padding-top: 1.5rem;
}

/* Status Badges */
.status-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pending {
  background-color: #fef3c7;
  color: #92400e;
}

.status-validated {
  background-color: #d1fae5;
  color: #065f46;
}

.status-approved {
  background-color: #d1fae5;
  color: #065f46;
}

.status-rejected {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-recorded {
  background-color: #dbeafe;
  color: #1e40af;
}

/* Type Badges */
:deep(.type-badge) {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

:deep(.type-badge.badge-success) {
  background-color: #d1fae5;
  color: #065f46;
}

:deep(.type-badge.badge-danger) {
  background-color: #fee2e2;
  color: #991b1b;
}

:deep(.type-badge i) {
  font-size: 0.875rem;
}

/* Currency Amounts in Grid */
:deep(.negative-amount) {
  color: #dc2626 !important;
  font-weight: 600 !important;
}

:deep(.positive-amount) {
  color: #059669 !important;
  font-weight: 600 !important;
}

:deep(.currency-amount) {
  font-weight: 600 !important;
}

/* Action Buttons */
:deep(.action-btn) {
  background: white;
  color: #64748b;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.5rem;
  transition: all 0.15s ease;
}

:deep(.validate-btn) {
  background-color: #d1fae5;
  color: #059669;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.validate-btn:hover) {
  background-color: #10b981;
  color: white;
}

:deep(.approve-btn) {
  background-color: #d1fae5;
  color: #059669;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.approve-btn:hover) {
  background-color: #10b981;
  color: white;
}

:deep(.reject-btn) {
  background-color: #fee2e2;
  color: #dc2626;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.reject-btn:hover) {
  background-color: #ef4444;
  color: white;
}

:deep(.edit-btn) {
  background-color: #fef3c7;
  color: #d97706;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.edit-btn:hover) {
  background-color: #fbbf24;
  color: white;
}

:deep(.cancel-btn) {
  background-color: #fee2e2;
  color: #dc2626;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.cancel-btn:hover) {
  background-color: #ef4444;
  color: white;
}

:deep(.view-btn) {
  background-color: #dbeafe;
  color: #2563eb;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.view-btn:hover) {
  background-color: #3b82f6;
  color: white;
}

:deep(.download-btn) {
  background-color: #e0e7ff;
  color: #4f46e5;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.download-btn:hover) {
  background-color: #6366f1;
  color: white;
}

/* Responsive Design */
@media (max-width: 1400px) {
  .finance-content-grid {
    grid-template-columns: 280px 1fr;
  }
}

@media (max-width: 1200px) {
  .finance-content-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 1fr;
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
  }

  .balance-amount {
    font-size: 2rem;
  }

  .balance-actions {
    flex-direction: column;
  }

  .balance-actions .btn {
    width: 100%;
  }

  .project-finance {
    padding: 1rem;
  }

  .transactions-section {
    padding: 1rem;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .tab-buttons {
    flex-wrap: wrap;
    width: 100%;
  }

  .tab-button {
    flex: 1;
    min-width: 100px;
    justify-content: center;
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }
  
  .finance-content-grid {
    gap: 1rem;
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Reject Provisioning Modal */
.reject-provisioning-modal {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.reject-provisioning-modal__header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.reject-provisioning-modal__icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reject-provisioning-modal__icon svg {
  width: 24px;
  height: 24px;
  color: #dc2626;
}

.reject-provisioning-modal__message {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.5;
}

.reject-provisioning-modal__details {
  background-color: #fef2f2;
  border-left: 4px solid #dc2626;
  padding: 1rem;
  border-radius: 0.375rem;
  display: flex;
  gap: 0.75rem;
}

.reject-provisioning-modal__details svg {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #dc2626;
  margin-top: 0.125rem;
}

.reject-provisioning-modal__details p {
  margin: 0;
  font-size: 0.875rem;
  color: #7f1d1d;
  line-height: 1.5;
}

.reject-provisioning-modal__notes {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.reject-provisioning-modal__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.reject-provisioning-modal__textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  transition: all 0.2s ease;
}

.reject-provisioning-modal__textarea:focus {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.reject-provisioning-modal__textarea::placeholder {
  color: #9ca3af;
}

.reject-provisioning-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

</style>
